import {
  Badge,
  Button,
  ConfirmationModalContent,
  ModalPopup,
  ReeligibilityInfo,
  Stack
} from 'components';
import dayjs from 'dayjs';
import { useFormikContext } from 'formik';
import { useParams } from 'react-router-dom';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { setChangeStatusModal } from 'store/clients/clientsSlice';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { useIsOnlyVisibleToUserWithPermissions } from 'utils/hooks/useIsOnlyVisibleTo';
import { notifyUserError } from 'utils/notifications';
import { PERMISSION } from 'utils/protectedRoutes/userRolesAndPermissionTypes';
import { useEffect } from 'react';
import { getPrograms } from 'store/clients/clientsActions';

import { ChangeStatusModal } from './changeStatusModal';
import { ManagerReview } from './managerReview';
import style from './profileInfo.module.scss';
import { Screener } from './screener';
import { Snapshot } from './snapshot';
import { HealthTracker } from './healthTracker';
import { MedicalVisits } from './medicalVisits';
import { Files } from './files';
import NewManagerReview from './managerReview/newManagerReview';
import NewestManagerReview from './managerReview/newestManagerReview';

const ProfileInfo = () => {
  const { client, changeStatusModal, chws, managers, programsList } = useAppSelector(
    (state) => state.clients
  );
  const { data } = client;
  const { dirty, submitForm, resetForm } = useFormikContext();
  const { enableBillingUpdates, enableDateTimeUpdates, enableMpiUpdate } = useFlags();
  const dispatch = useAppDispatch();
  const { clientId } = useParams();
  const isEdit = Boolean(clientId);
  const age = dayjs().diff(dayjs(client.data?.dob), 'year');
  const dob = dayjs(client.data?.dob).format('MM/DD/YYYY');
  const canUpdateManagerReview = useIsOnlyVisibleToUserWithPermissions([
    PERMISSION.UPDATE_MANAGER_REVIEW
  ]);
  const canViewMedicalVisits = data?.medicalVisitsEnabled;

  const handleUpdateStatusClick = () => {
    if (!clientId) {
      notifyUserError(
        'Please complete all required fields on the profile tab prior to changing status.'
      );
      return;
    } else if (dirty) {
      dispatch(setChangeStatusModal('confirm'));
    } else {
      dispatch(setChangeStatusModal('changeStatus'));
    }
  };

  const handleCloseModal = () => {
    dispatch(setChangeStatusModal(null));
  };

  const handleConfirm = async () => {
    await submitForm();
    dispatch(setChangeStatusModal('changeStatus'));
  };

  const handleReject = () => {
    dispatch(setChangeStatusModal('changeStatus'));
    resetForm();
  };

  useEffect(() => {
    dispatch(getPrograms({}));
  }, []);

  const currentChwName = [...chws.data, ...managers.data].find(
    (chw) => chw.id.toString() === data?.chwId?.toString()
  )?.name;
  const currentProgramName = programsList.find(
    (program) => program.id.toString() === data?.programId?.toString()
  )?.name;

  return (
    <>
      <Stack mt="26px" data-test-id="profile-info-name">
        <p className={style.clientName}>
          {isEdit ? `${data?.firstName} ${data?.lastName}` : 'New Client'}
        </p>
      </Stack>
      {clientId && (
        <Stack display="flex" alignItems="center" gap="8px">
          <p className={style.clientId}>DOB: {`${dob} (${age})`}</p>
          <p className={style.clientId}>
            {
              data?.primaryPhoneNumber
                // ?.replace(/([(])/g, '')
                ?.replace(/([)])/g, ') ')
              // ?.replace(/\s/g, '')
            }
          </p>
          <p className={style.clientId}>IMPaCT ID: {data?.prependedId}</p>
          {enableMpiUpdate && (
            <>
              <p className={style.clientId}>{currentProgramName}</p>
              <p className={style.clientId}>{currentChwName}</p>
            </>
          )}
        </Stack>
      )}
      {!enableMpiUpdate && (
        <Stack direction="row" alignItems="center" sx={{ lineHeight: '14px' }}>
          {data?.status?.id === 'PENDING' ? (
            <Badge text={data?.status?.name} type="warning" />
          ) : (
            <p className={style.status}>{isEdit ? data?.status?.name : 'Pre-Outreach'}</p>
          )}
          {data?.reEligibility?.available && (
            <ReeligibilityInfo
              text={`On ${dayjs(data?.reEligibility?.date).format(
                'MM/DD/YYYY'
              )} this client became eligible for ${data?.reEligibility?.programName}`}
              padding="0 0 0 8px"
            />
          )}
        </Stack>
      )}
      {data?.safetyFlag && (
        <div className={style.safety}>
          <span className="material-icons">warning_amber</span>
          Safety
        </div>
      )}
      <Stack gap="20px" width="100%" display="flex" alignItems="center">
        <Stack display="flex" gap="8px" alignItems="center">
          <Stack width={enableMpiUpdate ? 'auto' : '147px'} data-test-id="profile-update-status">
            <Button
              theme="secondary"
              size={enableMpiUpdate ? 'extraSmall' : undefined}
              handleOnClick={handleUpdateStatusClick}
              text={
                enableMpiUpdate ? (isEdit ? data?.status?.name : 'Pre-Outreach') : 'Update status'
              }
              customStyleClass={enableMpiUpdate ? style.updateStatusButton : ''}
            />
          </Stack>
          {canUpdateManagerReview && (
            <Stack width="136px" data-test-id="profile-update-status">
              {enableDateTimeUpdates ? (
                <NewestManagerReview />
              ) : enableBillingUpdates ? (
                <NewManagerReview />
              ) : (
                <ManagerReview />
              )}
            </Stack>
          )}
        </Stack>
        <Stack gap="16px" display="flex" width="100%">
          {isEdit && <Snapshot />}
          {isEdit && <Screener />}
          {isEdit && <HealthTracker />}
          {isEdit && canViewMedicalVisits && <MedicalVisits />}
          {isEdit && <Files />}
        </Stack>
      </Stack>

      <ModalPopup
        handleClose={handleCloseModal}
        open={changeStatusModal === 'confirm'}
        children={
          <ConfirmationModalContent
            title="You have unsaved changes"
            rejectButtonText="Discard changes"
            confirmButtonText="Save changes"
            handleConfirm={handleConfirm}
            handleReject={handleReject}
          />
        }
      />
      <ModalPopup
        customClassInsideContainer={style.modal}
        open={changeStatusModal === 'changeStatus'}
        handleClose={handleCloseModal}
      >
        <ChangeStatusModal onClose={handleCloseModal} />
      </ModalPopup>
    </>
  );
};

export default ProfileInfo;
